import { useState } from "react";
import { formatDate, calculateAge } from "../../utils/dateUtils";
import { api } from "@/lib/api";
import ViewDiagnosisRecordButton from "./viewDiagnosisRecord";
import { User, Calendar, Phone, Heart, History, ChevronDown, ChevronUp, FileText, ExternalLink, Pencil } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Patient, PatientDocument } from "@/types/types";
import { patientApi } from "@/api/patient";
import { ensureDoctorPrefix } from "@/lib/doctorName";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

type MedicalHistoryField = "allergy" | "chronicDisease" | "preExistingCondition";

const MEDICAL_HISTORY_FIELDS: {
  key: MedicalHistoryField;
  title: string;
  icon: string;
  ring: string;
  placeholder: string;
}[] = [
  {
    key: "allergy",
    title: "Allergies",
    icon: "🤧",
    ring: "ring-rose-100 hover:ring-rose-200",
    placeholder: "e.g. Penicillin, Sulfa drugs, Peanuts…",
  },
  {
    key: "chronicDisease",
    title: "Chronic diseases",
    icon: "🏥",
    ring: "ring-amber-100 hover:ring-amber-200",
    placeholder: "e.g. Diabetes, Hypertension, Asthma…",
  },
  {
    key: "preExistingCondition",
    title: "Pre-existing conditions",
    icon: "📋",
    ring: "ring-slate-100 hover:ring-slate-200",
    placeholder: "e.g. Previous surgery, congenital condition…",
  },
];

function PatientBasicDetails({ patientId }: { patientId: string }) {
  const queryClient = useQueryClient();
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editingField, setEditingField] = useState<MedicalHistoryField | null>(null);
  const [editValue, setEditValue] = useState("");

  const { data: patient } = useQuery<Patient>({
    queryKey: ['patient-details', patientId],
    queryFn: async () => {
      const response = await patientApi.getPatientById(patientId);
      return response;
    },
    enabled: !!patientId,
  });

  const { data: documents } = useQuery<PatientDocument[]>({
    queryKey: ['patient-documents', patientId],
    queryFn: async () => {
      const response = await patientApi.getDocuments(patientId);
      return response;
    },
    enabled: !!patientId,
  });

  const saveMedicalHistoryMutation = useMutation({
    mutationFn: async ({
      field,
      value,
    }: {
      field: MedicalHistoryField;
      value: string;
    }) => {
      const trimmed = value.trim();
      return patientApi.updatePatientDetails(patientId, {
        [field]: trimmed.length > 0 ? trimmed : null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-details', patientId] });
      toast.success("Medical history updated");
      setEditingField(null);
      setEditValue("");
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || "Failed to update medical history");
    },
  });

  const openEditDialog = (field: MedicalHistoryField) => {
    const current = patient?.[field];
    setEditingField(field);
    setEditValue(typeof current === "string" ? current : "");
  };

  const handleSaveMedicalHistory = () => {
    if (!editingField) return;
    saveMedicalHistoryMutation.mutate({ field: editingField, value: editValue });
  };

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await api.get(`/api/appointment/history?patientId=${patient?.id}`);
      setHistory(response.data.data);
    } catch (err) {
      setError("Failed to fetch patient history");
      console.error("Error fetching history:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewHistory = () => {
    setShowHistory(!showHistory);
    if (!showHistory) {
      fetchHistory();
    }
  };

  const getReferredByInfo = () => {
    if (patient?.registrationSource === 'REFERRAL' || patient?.registrationSource === 'AFFILIATE') {
      return patient?.registrationSourceDetails || 'Not specified';
    }
    return null;
  };

  const editingMeta = MEDICAL_HISTORY_FIELDS.find((f) => f.key === editingField);

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 bg-gradient-to-r from-slate-900 to-slate-800 px-5 py-4 text-white sm:px-6">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20">
            <User className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold sm:text-xl">
              {patient?.name}{' '}
              <span className="font-normal text-slate-300">· {patient?.uhid}</span>
            </h2>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-slate-300">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {(() => {
                  const age = calculateAge(patient?.dob);
                  const sex = patient?.gender;
                  if (age == null && !sex) return "—";
                  if (age == null) return String(sex);
                  return sex ? `${age} years, ${sex}` : `${age} years`;
                })()}
              </span>
              <span className="flex items-center gap-1">
                <Phone className="w-3 h-3" />
                {patient?.phone}
              </span>
              <span className="rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-medium text-white ring-1 ring-white/25">
                {patient?.registrationMode}
              </span>
            </div>
          </div>
        </div>

        {getReferredByInfo() && (
          <div className="hidden text-right sm:block">
            <div className="text-xs text-slate-400">Referred by</div>
            <div className="text-sm font-medium text-teal-200">{getReferredByInfo()}</div>
          </div>
        )}
      </div>

      <div className="space-y-5 p-5 sm:p-6">
      <div>
        <div className="mb-3 flex items-center gap-2">
          <Heart className="h-4 w-4 text-rose-500" />
          <h3 className="text-sm font-semibold text-slate-800">Medical history</h3>
          <span className="text-xs text-slate-400">Click a card to add or edit</span>
        </div>

        <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
          {MEDICAL_HISTORY_FIELDS.map(({ key, title, icon, ring }) => {
            const value = patient?.[key];
            const isEmpty = !value;
            return (
              <button
                key={key}
                type="button"
                onClick={() => openEditDialog(key)}
                className={`group flex w-full items-start gap-3 rounded-xl border border-slate-100 bg-slate-50/80 p-3 text-left ring-1 transition-all hover:border-slate-200 hover:bg-white hover:shadow-sm ${ring}`}
              >
                <span className="text-lg leading-none">{icon}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</div>
                    <Pencil className="h-3.5 w-3.5 shrink-0 text-slate-300 opacity-0 transition-opacity group-hover:opacity-100" />
                  </div>
                  <div className={`mt-1 text-sm ${isEmpty ? 'italic text-slate-400' : 'text-slate-800'}`}>
                    {value || 'None recorded'}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="border-t border-slate-100 pt-2">
        <button
          onClick={handleViewHistory}
          className="flex items-center justify-between w-full p-2 hover:bg-gray-50 transition-colors rounded-lg"
        >
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-purple-500" />
            <span className="text-sm font-medium text-gray-800">Appointment History</span>
            {history.length > 0 && (
              <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2 py-0.5 rounded-full">
                {history.length}
              </span>
            )}
          </div>
          {showHistory ? (
            <ChevronUp className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </button>

        {showHistory && (
          <div className="mt-3">
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-artis-gold"></div>
                <span className="ml-2 text-sm text-gray-600">Loading...</span>
              </div>
            ) : error ? (
              <div className="text-center py-4">
                <div className="text-red-500 text-sm mb-1">⚠️ {error}</div>
                <button
                  onClick={fetchHistory}
                  className="text-blue-500 hover:text-blue-700 text-xs underline"
                >
                  Try again
                </button>
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                <History className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No appointment history found</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.map((appointment: any) => (
                  <div key={appointment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-4 text-sm">
                        <span className="font-medium text-gray-900">
                          {formatDate(appointment.scheduledAt)}
                        </span>
                        <span className="text-gray-600">
                          {ensureDoctorPrefix(appointment.doctor.name)}
                        </span>
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                          {appointment.doctor.specialisation}
                        </span>
                      </div>
                      <div className="text-xs thms-page-subtitle truncate">
                        {appointment?.diagnosisRecord?.diagnosis || 'No diagnosis recorded'}
                      </div>
                    </div>
                    <div className="ml-3">
                      {appointment?.diagnosisRecord ? (
                        <ViewDiagnosisRecordButton appointmentId={appointment.id} />
                      ) : (
                        <div className="flex flex-col gap-1">
                          {documents && documents.length > 0 ? (
                            documents.map((doc) => (
                              <button
                                key={doc.id}
                                onClick={() => window.open(doc.url, '_blank')}
                                className="flex items-center gap-1 text-blue-500 hover:text-blue-700 text-xs underline"
                              >
                                <FileText className="w-3 h-3" />
                                {doc.type.replace('_', ' ')}
                                <ExternalLink className="w-3 h-3" />
                              </button>
                            ))
                          ) : (
                            <span className="text-gray-400 text-xs">No records</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      </div>

      <Dialog open={!!editingField} onOpenChange={(open) => !open && setEditingField(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit {editingMeta?.title}</DialogTitle>
          </DialogHeader>
          <Textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            placeholder={editingMeta?.placeholder}
            rows={4}
            className="resize-none"
          />
          <p className="text-xs text-slate-500">
            Leave empty and save to clear this field. Changes are saved to the patient record.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setEditingField(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveMedicalHistory}
              disabled={saveMedicalHistoryMutation.isPending}
              className="bg-teal-700 hover:bg-teal-800"
            >
              {saveMedicalHistoryMutation.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PatientBasicDetails;
